import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Divider,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Translate as TranslateIcon,
  Edit,
  Delete,
  Add,
} from '@mui/icons-material';
import { footerService, globalService } from '../../services/firebase';
import { FooterData, FooterLink, TranslatableField } from '../../types';
import { getTranslatedData } from '../../utils/getTranslatedData';
import { produce } from 'immer';
import { useTranslation } from 'react-i18next';

type LinkType = 'quickLinks' | 'supportLinks' | 'bottomLinks';


const ManageFooterPage: React.FC = () => {
  const [footerData, setFooterData] = useState<FooterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedLang, setSelectedLang] = useState('tr');
  const { t } = useTranslation();

  // Link editing dialog state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [currentLink, setCurrentLink] = useState<Partial<FooterLink> | null>(null);
  const [currentLinkType, setCurrentLinkType] = useState<LinkType>('quickLinks');
  
  useEffect(() => {
    fetchFooterData();
  }, []);

  const fetchFooterData = async () => {
    try {
      setLoading(true);
      setError(null);
      let data = await footerService.getFooterData();
      
      if (!data) {
        await footerService.initializeFooterData();
        data = await footerService.getFooterData();
      }
      
      setFooterData(data);
    } catch (err: any) {
      setError('Footer verileri yüklenirken hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any, isTranslatable: boolean = false) => {
    if (!footerData) return;

    if (isTranslatable) {
      // Nested objeler için özel işlem
      const keys = field.split('.');
      const newFooterData = produce(footerData, (draft: any) => {
        let current: any = draft;
        for (let i = 0; i < keys.length - 1; i++) {
          current = current[keys[i]] = current[keys[i]] || {};
        }
        const fieldName = keys[keys.length - 1];
        const currentField = current[fieldName];
        
        if (typeof currentField === 'object' && currentField !== null && !Array.isArray(currentField)) {
          // Eğer alan zaten bir obje ise, ilgili dilin değerini güncelle
          current[fieldName] = {
            ...currentField,
            [selectedLang]: value,
          };
        } else {
          // Eğer alan bir string veya tanımsız ise, yeni bir obje oluştur
          const existingValue = currentField || '';
          current[fieldName] = {
            tr: selectedLang === 'tr' ? value : (typeof currentField === 'string' ? existingValue : ''),
            en: selectedLang === 'en' ? value : (typeof currentField === 'string' ? existingValue : ''),
            ar: selectedLang === 'ar' ? value : (typeof currentField === 'string' ? existingValue : ''),
          };
        }
      });
      setFooterData(newFooterData);
    } else {
      // Use a generic approach to set nested properties
      const keys = field.split('.');
      const newFooterData = produce(footerData, (draft: any) => {
        let current: any = draft;
        for (let i = 0; i < keys.length - 1; i++) {
          current = current[keys[i]] = current[keys[i]] || {};
        }
        current[keys[keys.length - 1]] = value;
      });
      setFooterData(newFooterData);
    }
  };

  const handleSave = async () => {
    if (!footerData) return;
    try {
      setSaving(true);
      setError(null);
      await footerService.updateFooterData(footerData);
      setSuccess('Footer başarıyla güncellendi!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Kaydetme hatası:', err);
      setError(`Kaydetme sırasında bir hata oluştu: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenLinkDialog = (type: LinkType, link?: FooterLink) => {
    setCurrentLinkType(type);
    setCurrentLink(link ? {...link} : { text: { tr: '', en: '', ar: '' }, path: '', isActive: true });
    setLinkDialogOpen(true);
  };

  const handleCloseLinkDialog = () => {
    setLinkDialogOpen(false);
    setCurrentLink(null);
  };

  const handleLinkInputChange = (field: keyof FooterLink, value: any, isTranslatable: boolean = false) => {
    if (!currentLink) return;

    if (isTranslatable) {
      setCurrentLink(prev => {
        if (!prev) return null;
        const currentField = (prev as any)[field];
        if (typeof currentField === 'object' && currentField !== null && !Array.isArray(currentField)) {
          return {
            ...prev,
            [field]: {
              ...currentField,
              [selectedLang]: value,
            }
          };
        } else {
          const existingValue = currentField || '';
          return {
            ...prev,
            [field]: {
              tr: selectedLang === 'tr' ? value : (typeof currentField === 'string' ? existingValue : ''),
              en: selectedLang === 'en' ? value : (typeof currentField === 'string' ? existingValue : ''),
              ar: selectedLang === 'ar' ? value : (typeof currentField === 'string' ? existingValue : ''),
            }
          };
        }
      });
    } else {
      setCurrentLink(prev => {
        if (!prev) return null;
        return { ...prev, [field]: value };
      });
    }
  };
  
  const handleSaveLink = () => {
    if (!footerData || !currentLink) return;

    const newFooterData = produce(footerData, (draft: any) => {
      const links = draft[currentLinkType] as FooterLink[];
      if (currentLink.id) { // Editing existing link
        const linkIndex = links.findIndex(l => l.id === currentLink.id);
        if (linkIndex > -1) {
          links[linkIndex] = currentLink as FooterLink;
        }
      } else { // Adding new link
        links.push({
          ...currentLink,
          id: Date.now().toString(),
          order: links.length + 1,
        } as FooterLink);
      }
    });

    setFooterData(newFooterData);
    handleCloseLinkDialog();
  };
  
  const handleDeleteLink = (type: LinkType, linkId: string) => {
      const newFooterData = produce(footerData, (draft: any) => {
          (draft[type] as FooterLink[]) = (draft[type] as FooterLink[]).filter(link => link.id !== linkId);
      });
      setFooterData(newFooterData);
  };

  const handleDynamicListChange = (path: string, index: number, value: string) => {
    const newFooterData = produce(footerData, (draft: any) => {
        const keys = path.split('.');
        let current: any = draft;
        for (let i = 0; i < keys.length; i++) {
             current = current[keys[i]];
        }
        current[index] = value;
    });
    setFooterData(newFooterData);
  };

  const addDynamicListItem = (path: string) => {
      const newFooterData = produce(footerData, (draft: any) => {
          const keys = path.split('.');
          let current: any = draft;
          for (let i = 0; i < keys.length; i++) {
              current = current[keys[i]];
          }
          current.push('');
      });
      setFooterData(newFooterData);
  };

  const removeDynamicListItem = (path: string, index: number) => {
      const newFooterData = produce(footerData, (draft: any) => {
          const keys = path.split('.');
          let current: any = draft;
          for (let i = 0; i < keys.length; i++) {
              current = current[keys[i]];
          }
          current.splice(index, 1);
      });
      setFooterData(newFooterData);
  };

  const handleTranslateAll = async () => {
    if (!footerData) return;
    setSaving(true);
    setError(null);

    try {
      let dataCopy = JSON.parse(JSON.stringify(footerData));

      // Şirket bilgileri
      const companyFields = ['companyInfo.name', 'companyInfo.description'];
      for (const path of companyFields) {
        const keys = path.split('.');
        let parent: any = dataCopy;
        for (let i = 0; i < keys.length - 1; i++) parent = parent[keys[i]];
        const fieldName = keys[keys.length - 1];
        const sourceText = getTranslatedData(parent[fieldName], 'tr');
        if (sourceText) {
          const translations = await globalService.translateTexts({ text: sourceText, sourceLang: 'tr', targetLangs: ['en','ar'] });
          parent[fieldName] = { tr: sourceText, en: (translations as any).en, ar: (translations as any).ar };
        }
      }

      // Link metinleri
      const linkGroups: ('quickLinks'|'supportLinks'|'bottomLinks')[] = ['quickLinks','supportLinks','bottomLinks'];
      for (const group of linkGroups) {
        if (Array.isArray(dataCopy[group])) {
          for (const link of dataCopy[group]) {
            const sourceText = getTranslatedData(link.text, 'tr');
            if (sourceText) {
              const translations = await globalService.translateTexts({ text: sourceText, sourceLang: 'tr', targetLangs: ['en','ar'] });
              link.text = { tr: sourceText, en: (translations as any).en, ar: (translations as any).ar };
            }
          }
        }
      }

      setFooterData(dataCopy);
      setSuccess('Tüm alanlar başarıyla çevrildi!');
    } catch (err) {
      console.error(err);
      setError('Çeviri sırasında bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
  }

  if (!footerData) {
    return <Alert severity="error">Footer verileri yüklenemedi.</Alert>;
  }
  
  const renderLinkGroup = (title: string, type: LinkType) => (
    <Card sx={{ mb: 3 }}>
        <CardContent>
            <Typography variant="h6">{title}</Typography>
            <Divider sx={{ my: 1 }}/>
            {footerData[type]?.map((link) => (
                <Paper key={link.id} sx={{ p: 1, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography>{getTranslatedData(link.text, selectedLang)} ({link.path})</Typography>
                        <Chip label={link.isActive ? 'Aktif' : 'Pasif'} size="small" color={link.isActive ? 'success' : 'default'} />
                    </Box>
                    <Box>
                        <IconButton onClick={() => handleOpenLinkDialog(type, link)}><Edit/></IconButton>
                        <IconButton onClick={() => handleDeleteLink(type, link.id)}><Delete/></IconButton>
                    </Box>
                </Paper>
            ))}
            <Button startIcon={<Add/>} onClick={() => handleOpenLinkDialog(type)}>Yeni Link Ekle</Button>
        </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Footer Yönetimi</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button variant="outlined" onClick={handleTranslateAll} disabled={saving} startIcon={saving ? <CircularProgress size={20}/> : <TranslateIcon/>}>
              Tümünü Çevir
            </Button>
            <ToggleButtonGroup
                value={selectedLang}
                exclusive
                onChange={(e, newLang) => newLang && setSelectedLang(newLang)}
                aria-label="text alignment"
                size="small"
            >
                <ToggleButton value="tr" aria-label="left aligned">TR</ToggleButton>
                <ToggleButton value="en" aria-label="centered">EN</ToggleButton>
                <ToggleButton value="ar" aria-label="right aligned">AR</ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving || loading}
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            >
                {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
            </Button>
        </Box>
      </Box>
      
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}
      
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
            <Card>
                <CardContent>
                    <Typography variant="h6">Şirket Bilgileri</Typography>
                    <Divider sx={{ my: 1 }}/>
                    <TextField
                        label="Şirket Adı"
                        fullWidth
                        value={getTranslatedData(footerData.companyInfo.name, selectedLang)}
                        onChange={(e) => handleInputChange('companyInfo.name', e.target.value, true)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Kısa Açıklama"
                        fullWidth
                        multiline
                        rows={3}
                        value={getTranslatedData(footerData.companyInfo.description, selectedLang)}
                        onChange={(e) => handleInputChange('companyInfo.description', e.target.value, true)}
                    />
                </CardContent>
            </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
            <Card>
                <CardContent>
                    <Typography variant="h6">İletişim Bilgileri</Typography>
                    <Divider sx={{ my: 1 }}/>
                    <TextField
                        label="Adres"
                        fullWidth
                        multiline
                        rows={3}
                        value={getTranslatedData(footerData.contactInfo.address, selectedLang)}
                        onChange={(e) => handleInputChange('contactInfo.address', e.target.value, true)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="WhatsApp Numarası"
                        fullWidth
                        value={footerData.contactInfo.whatsapp}
                        onChange={(e) => handleInputChange('contactInfo.whatsapp', e.target.value)}
                    />
                </CardContent>
            </Card>
        </Grid>

        <Grid size={{ xs: 12 }}>
            <Card>
                <CardContent>
                    <Typography variant="h6">Telefon Numaraları</Typography>
                    {footerData.contactInfo.phones.map((phone, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <TextField
                                fullWidth
                                value={phone}
                                onChange={(e) => handleDynamicListChange('contactInfo.phones', index, e.target.value)}
                            />
                            <IconButton onClick={() => removeDynamicListItem('contactInfo.phones', index)}>
                                <Delete />
                            </IconButton>
                        </Box>
                    ))}
                    <Button startIcon={<Add />} onClick={() => addDynamicListItem('contactInfo.phones')}>
                        Yeni Telefon Numarası Ekle
                    </Button>
                </CardContent>
            </Card>
        </Grid>

        <Grid size={{ xs: 12 }}>
            <Card>
                <CardContent>
                    <Typography variant="h6">Email Adresleri</Typography>
                    {footerData.contactInfo.emails.map((email, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <TextField
                                fullWidth
                                value={email}
                                onChange={(e) => handleDynamicListChange('contactInfo.emails', index, e.target.value)}
                            />
                            <IconButton onClick={() => removeDynamicListItem('contactInfo.emails', index)}>
                                <Delete />
                            </IconButton>
                        </Box>
                    ))}
                    <Button startIcon={<Add />} onClick={() => addDynamicListItem('contactInfo.emails')}>
                        Yeni Email Ekle
                    </Button>
                </CardContent>
            </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
            <Card>
                <CardContent>
                    <Typography variant="h6">Sosyal Medya</Typography>
                    <Divider sx={{ my: 1 }}/>
                    <TextField label="Facebook" fullWidth value={footerData.socialMedia?.facebook} onChange={(e) => handleInputChange('socialMedia.facebook', e.target.value)} sx={{ mb: 2 }}/>
                    <TextField label="Instagram" fullWidth value={footerData.socialMedia?.instagram} onChange={(e) => handleInputChange('socialMedia.instagram', e.target.value)} sx={{ mb: 2 }}/>
                    <TextField label="Twitter" fullWidth value={footerData.socialMedia?.twitter} onChange={(e) => handleInputChange('socialMedia.twitter', e.target.value)} sx={{ mb: 2 }}/>
                    <TextField label="YouTube" fullWidth value={footerData.socialMedia?.youtube} onChange={(e) => handleInputChange('socialMedia.youtube', e.target.value)} sx={{ mb: 2 }}/>
                    <TextField label="WhatsApp (Sosyal)" fullWidth value={footerData.socialMedia?.whatsapp} onChange={(e) => handleInputChange('socialMedia.whatsapp', e.target.value)} />
                </CardContent>
            </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
            <Card>
                <CardContent>
                    <Typography variant="h6">Copyright</Typography>
                    <Divider sx={{ my: 1 }}/>
                    <TextField
                        label="Copyright Metni"
                        fullWidth
                        value={getTranslatedData(footerData.copyright, selectedLang)}
                        onChange={(e) => handleInputChange('copyright', e.target.value, true)}
                    />
                </CardContent>
            </Card>
        </Grid>

        <Grid size={{ xs: 12 }}>
            {renderLinkGroup("Hızlı Linkler", "quickLinks")}
        </Grid>
        <Grid size={{ xs: 12 }}>
            {renderLinkGroup("Destek Linkleri", "supportLinks")}
        </Grid>
        <Grid size={{ xs: 12 }}>
            {renderLinkGroup("Alt Linkler", "bottomLinks")}
        </Grid>
      </Grid>
      
      <Dialog open={linkDialogOpen} onClose={handleCloseLinkDialog} fullWidth maxWidth="sm">
        <DialogTitle>{currentLink?.id ? 'Linki Düzenle' : 'Yeni Link Ekle'}</DialogTitle>
        <DialogContent>
            <TextField
                autoFocus
                margin="dense"
                label="Link Metni"
                fullWidth
                value={getTranslatedData(currentLink?.text, selectedLang)}
                onChange={(e) => handleLinkInputChange('text', e.target.value, true)}
                sx={{ mb: 2 }}
            />
            <TextField
                margin="dense"
                label="Link Yolu (örn: /hakkimizda)"
                fullWidth
                value={currentLink?.path || ''}
                onChange={(e) => handleLinkInputChange('path', e.target.value)}
                sx={{ mb: 2 }}
            />
            <FormControlLabel
                control={<Switch checked={currentLink?.isActive || false} onChange={(e) => handleLinkInputChange('isActive', e.target.checked)} />}
                label="Aktif"
            />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLinkDialog}>İptal</Button>
          <Button onClick={handleSaveLink}>Kaydet</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageFooterPage;